import {
  ChangeEvent,
  ClipboardEvent,
  createContext,
  EventHandler,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  MouseEventHandler,
  SyntheticEvent,
  useContext,
  useMemo,
} from "react";
import ShareholdersStore from "./ShareholdersStore";
import SearchController from "./SearchController";
import { I18nState, initialI18nState, PreppedI18nState, t } from "../i18n/i18nDomain";
import UserStore from "./UserStore";
import { RootStore } from "./RootStore";
import { DCEvent, EventDispatcher } from "../events/eventsDomain";
import { UserPrefs } from "./userPrefsDomain";
import { Language } from "../i18n/languages";
import { createLocaleFormat } from "../i18n/localeFormat";
import { partial } from "lodash";
import { Whitelabel } from "../common/common";

export const RootStoreContext = createContext({} as RootStore);

export const useRootStore = (): RootStore => useContext(RootStoreContext);
export const useI18n = (overrideLang?: Language): PreppedI18nState => {
  const defaultI18n = useRootStore().i18n ?? initialI18nState();
  return useMemo<PreppedI18nState>(() => {
    const i18n: I18nState = overrideLang ? { ...defaultI18n, lang: overrideLang } : defaultI18n;
    return {
      lang: i18n.lang,
      f: createLocaleFormat(i18n.lang),
      t: partial(t, i18n),
      tf: (k) => {
        if (typeof k === "string" || typeof k === "number" || k === null || k === undefined) {
          return String(k);
        }
        return t(i18n, k);
      },
    };
  }, [defaultI18n, overrideLang]);
};
export const useUserStore = (): UserStore => useRootStore().userStore;
export const useShareholdersStore = (): ShareholdersStore => useRootStore().shareholdersStore;
export const useSearchStore = (): SearchController => useRootStore().searchStore;
export const useShareTypesStore = () => useRootStore().shareTypesStore;
export const useArticleStore = () => useRootStore().articleStore;
export const useMessageStore = () => useRootStore().messageStore;
export const usePortfolioStore = () => useRootStore().portfolioStore;
export const useDialogStore = () => useRootStore().dialogStore;
export const useTranslation = (lang?: Language) => useI18n(lang);
export const useLanguage = () => useI18n().lang;
export const useLocaleFormat = (lang?: Language) => useI18n(lang).f;
export const useUserPrefs = (): UserPrefs => useRootStore().userPrefs ?? {};
export const useWhitelabel = (): Whitelabel | undefined =>
  useRootStore().userStore.companyInfo?.company.whitelabel;

export const DispatchContext = createContext<EventDispatcher>(() => {
  throw new Error("DispatchContext not initialized");
});
export const useDispatch = (): EventDispatcher => useContext(DispatchContext);

export type DCDomEventHandler<E extends SyntheticEvent<T>, T = Element> =
  | ((event: E, dispatch: EventDispatcher) => void | DCEvent[] | unknown)
  | DCEvent[];

export type DCMouseEventHandler<T = Element> = DCDomEventHandler<MouseEvent<T>, T>;
export type DCFocusEventHandler<T = Element> = DCDomEventHandler<FocusEvent<T>, T>;
export type DCChangeEventHandler<T = Element> = DCDomEventHandler<ChangeEvent<T>, T>;
export type DCKeyboardEventHandler<T = Element> = DCDomEventHandler<KeyboardEvent<T>, T>;
export type DCClipboardEventHandler<T = Element> = DCDomEventHandler<ClipboardEvent<T>, T>;

type DCCallbackHandler = (
  dispatch: EventDispatcher,
  ...args: never[]
) => void | DCEvent[] | unknown;
export type DCCallback<T extends DCCallbackHandler = () => void> = T | DCEvent[];

export const useDomEventHandler = <E extends SyntheticEvent<T>, T = Element>(
  handler?: DCDomEventHandler<E, T>,
): EventHandler<E> | undefined => {
  const dispatch = useDispatch();
  return !handler
    ? undefined
    : (event: E) => {
        if (Array.isArray(handler)) {
          dispatch(handler);
        } else {
          const retVal = handler(event, dispatch);
          if (Array.isArray(retVal)) {
            dispatch(retVal);
          }
        }
      };
};

export const useFocusEventHandler = <T = Element>(handler?: DCFocusEventHandler<T>) =>
  useDomEventHandler<FocusEvent<T>, T>(handler);

export const useChangeEventHandler = <T = Element>(handler?: DCChangeEventHandler<T>) =>
  useDomEventHandler<ChangeEvent<T>, T>(handler);

export const useKeyboardEventHandler = <T = Element>(handler?: DCKeyboardEventHandler<T>) =>
  useDomEventHandler<KeyboardEvent<T>, T>(handler);

export const useClipboardEventHandler = <T = Element>(handler?: DCClipboardEventHandler<T>) =>
  useDomEventHandler<ClipboardEvent<T>, T>(handler);

export const useMouseEventHandler = <T = Element>(
  handler?: DCMouseEventHandler<T>,
): MouseEventHandler<T> | undefined => {
  const dispatch = useDispatch();
  return !handler
    ? undefined
    : (event: MouseEvent<T>) => {
        if (Array.isArray(handler)) {
          event.preventDefault();
          event.stopPropagation();
          dispatch(handler);
        } else {
          const retVal = handler(event, dispatch);
          if (Array.isArray(retVal)) {
            event.preventDefault();
            event.stopPropagation();
            dispatch(retVal);
          }
        }
      };
};

export const useCallbackHandler = (handler?: ((dispatch: EventDispatcher) => void) | DCEvent[]) => {
  const dispatch = useDispatch();
  if (!handler) {
    return undefined;
  } else if (Array.isArray(handler)) {
    return () => {
      dispatch(handler);
    };
  } else {
    return () => {
      handler(dispatch);
    };
  }
};
